import Head from 'next/head';
import type { GetStaticProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from '../_app';
import React, { useMemo } from 'react';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import Layout from '@/layouts';
import { canonicalUrl } from '@/shared/utils';
import { getTdkApi } from '@/lib/service/tdk';
import { fetchLoadingDataWith } from '@/lib/service';
import { page3FaqJsonLd } from '@/lib/jsonld';
import { hyphenatedToTitleCase, removeLastPathSegment } from '@/shared/path-helper';
import { getFaqList } from '@/lib/utils/landingData';
import { getRevalidateTime, SHOW_ISR_DEBUG_PANEL, SITE_HOST, ISR_ENV } from '@/shared/isr-config';
import ISRDebugPanel from '@/components/Common/ISRDebugPanel';

const LandingPageMainContent = dynamic(() => import('@/components/PostComments'), {
  ssr: true,
});

export const getStaticProps: GetStaticProps = async (context) => {
  const { locale } = context;

  const path = '/instagram-viewer/post-comments';

  const [localeRes, tdkRes, dataResRaw] = await Promise.all([
    serverSideTranslations(locale, ['common']),
    getTdkApi(path, locale),
    fetchLoadingDataWith(path, locale).catch((): null => null),
  ]);

  // INFO: 如果当前语言没有配置数据，fallback 到英文
  let dataRes = dataResRaw;
  if (!dataRes?.data && locale !== 'en') {
    dataRes = await fetchLoadingDataWith(path, 'en').catch((): null => null);
  }

  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};
  const parentPath = removeLastPathSegment(path);
  const siteUrl = locale !== 'en' ? `https://${SITE_HOST}/${locale}` : `https://${SITE_HOST}`;
  const parentUrl = siteUrl + parentPath;
  const parentTitle = hyphenatedToTitleCase(parentPath);

  // INFO: 网络不稳定时 dataRes 可能为 null，或者 API 返回的数据中 faqList 为空
  // getFaqList 内部无法处理这种情况，所以在调用前进行完整检查
  const hasFaqData = dataRes?.data?.faqList?.content_list || dataRes?.data?.faqList?.category_list;
  const resultFaqList = hasFaqData ? getFaqList(dataRes) : [];

  const jsonLd = page3FaqJsonLd(
    {
      title: hyphenatedToTitleCase(`post-comments`),
      desc: tdkRes?.data?.description ?? tempData?.head_Meta_Desc ?? null,
      url: canonicalUrl(SITE_HOST, path, locale),
    },
    {
      title: parentTitle,
      url: parentUrl,
    },
    {
      url: siteUrl,
    },
    resultFaqList,
    {
      name: 'Instagram Post Comments Viewer — DolphinRadar',
      url: 'https://www.dolphinradar.com/instagram-viewer/post-comments',
      applicationCategory: 'Instagram Viewer',
      description:
        "Explore Instagram post comments with Dolphin Radar's IG Comment Viewer. Fast, simple access to public comments across Reels, photos, and more.",
      featureList: [
        'Easily View Public Post Comments',
        'Better Engagement Research',
        'No Instagram Login Required',
        'Rich Data',
      ],
    }
  );

  // INFO: 注释掉 redirect 逻辑，因为在 getStaticProps (ISR) 构建时，
  // 如果网络不稳定导致 fetchLoadingDataWith 请求失败，dataRes 会为 null，
  // 此时触发 redirect 会导致构建失败。
  // 暂时允许 contentData 为空，页面组件应能处理空数据情况。
  // if (!dataRes?.data) {
  //   return {
  //     redirect: {
  //       destination: `/`,
  //       permanent: false,
  //     },
  //   };
  // }

  return {
    props: {
      ...localeRes,
      herf: canonicalUrl(SITE_HOST, path, locale),
      locale,
      title: tdkRes?.data?.title ?? tempData?.head_Meta_Title ?? null,
      description: tdkRes?.data?.description ?? tempData?.head_Meta_Desc ?? null,
      contentData: dataRes?.data ?? null,
      jsonLd,
      // ISR 调试信息
      __isr_generated_at: Date.now(),
      __isr_revalidate: getRevalidateTime('instagram-viewer/post-comments'),
      __isr_page_name: 'instagram-viewer/post-comments',
      __isr_env: ISR_ENV,
    },
    revalidate: getRevalidateTime('instagram-viewer/post-comments'),
  };
};

const PageContent: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = (props) => {
  return useMemo(
    () => (
      <>
        <LandingPageMainContent data={props} />
        {SHOW_ISR_DEBUG_PANEL && (
          <ISRDebugPanel
            pageName={props.__isr_page_name}
            generatedAt={props.__isr_generated_at}
            revalidate={props.__isr_revalidate}
            env={props.__isr_env}
          />
        )}
      </>
    ),
    [props]
  );
};

PageContent.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta property="og:image" content={AppSettings.logoUrl} />
          <meta property="og:image:secure_url" content={AppSettings.logoUrl} />
          <meta property="og:type" content="summary" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta name="twitter:image" content={AppSettings.logoUrl} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />
          {process.env.NEXT_APP_ENV !== 'production' && <meta name="robots" content="noindex,nofollow" />}
          <link rel="canonical" href={page.props?.herf} />
          <link rel="icon" href={AppSettings.icoPath} />
          {page.props?.jsonLd && (
            <script
              async
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(page.props?.jsonLd) }}
            />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default PageContent;
